import {
  ISourceEntityTypes,
  SourceEntityMigrationType,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISODateType,
  Timezone,
  TypeGuard,
  isISODateType,
} from '@principle-theorem/shared';
import { isNumber, isString } from 'lodash';
import { PATIENT_INTERACTION_DESTINATION_ENTITY } from '../../../destination/entities/patient-interactions';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';

export const PATIENT_LETTER_RESOURCE_TYPE = 'patientLetter';

export const PATIENT_LETTER_SOURCE_ENTITY = SourceEntity.init({
  metadata: {
    label: 'Patient Letter List',
    description: '',
    idPrefix: PATIENT_LETTER_RESOURCE_TYPE,
    migrationType: SourceEntityMigrationType.Automatic,
  },
});

export interface IOasisPatientLetter {
  patientId: number;
  id: number;
  patientDocumentNumber: number;
  description: string;
  createdAt: ISODateType;
  branchNumber?: number;
  fileFormat:
    | 'DOC'
    | 'DOCX'
    | 'HTM'
    | 'JPEG'
    | 'JPG'
    | 'ORPT'
    | 'PDF'
    | 'PNG'
    | 'TIF'
    | 'VPD'
    | 'XSLX';
  fileName: string;
  blobLength?: number;
  blob?: string;
  imageType: string;
}

function isOasisPatientLetter(
  data: unknown,
  logError: boolean
): data is IOasisPatientLetter {
  return TypeGuard.interface<IOasisPatientLetter>(
    {
      patientId: isNumber,
      id: isNumber,
      patientDocumentNumber: isNumber,
      description: isString,
      fileFormat: isString,
      fileName: isString,
      blobLength: TypeGuard.nilOr(isNumber),
      blob: TypeGuard.nilOr(isString),
      branchNumber: TypeGuard.nilOr(isNumber),
      imageType: isString,
      createdAt: isISODateType,
    },
    logError
  )(data);
}

export interface IOasisPatientLetterFilters {
  patientId: number;
}

export interface IOasisPatientCommuncationTranslations {
  createdAt: ISODateType;
}

const PATIENT_LETTER_SOURCE_QUERY = `
SELECT
  convert_to_integer(PATNUMBER) AS patient_id,
  SEQNUMBER AS id,
  DOCUMENTNUMBER AS patient_document_number,
  normalise_oasis_date_field(DOCUMENTDATE) AS created_at,
  DETAILS AS description,
  DOCUMENTTYPE AS file_format,
  BRANCHNUMBER AS branch_number,
  BLOBLEN AS blob_length,
  NULLIF(BLOB, '[NULL]') AS blob,
  CONCAT(BLOBNUMBER, '.', DOCUMENTTYPE) AS file_name,
  IMAGETYPE::TEXT AS image_type
FROM PBLETTER
WHERE
  PATNUMBER IS NOT NULL
ORDER BY id
`;

/**
 * Oasis v8 variant: PBLETTER only has SKEY, PATNUMBER, DOCUMENTNUMBER, DATE,
 * DETAILS and TYPE. Files are named by the zero-padded 8 digit SKEY, with the
 * extension implied by the single-letter TYPE. Report archives (R) are skipped.
 */
const PATIENT_LETTER_SOURCE_QUERY_V8 = `
SELECT
  letter.*,
  CONCAT(LPAD(letter.id::TEXT, 8, '0'), NULLIF(CONCAT('.', letter.file_format), '.')) AS file_name
FROM (
  SELECT
    convert_to_integer(PATNUMBER) AS patient_id,
    convert_to_integer(SKEY) AS id,
    convert_to_integer(DOCUMENTNUMBER) AS patient_document_number,
    normalise_oasis_date_field(DATE) AS created_at,
    COALESCE(DETAILS, '') AS description,
    CASE TRIM(TYPE)
      WHEN 'W' THEN 'DOC'
      WHEN 'A' THEN 'PDF'
      WHEN 'J' THEN 'JPG'
      WHEN 'K' THEN 'JPG'
      WHEN 'H' THEN 'HTM'
      ELSE ''
    END AS file_format,
    '' AS image_type
  FROM PBLETTER
  WHERE
    SKEY IS NOT NULL
    AND PATNUMBER IS NOT NULL
    AND COALESCE(TRIM(TYPE), '') <> 'R'
) AS letter
ORDER BY id
`;

export type OasisPatientLetter = ISourceEntityTypes<
  IOasisPatientLetter,
  IOasisPatientCommuncationTranslations,
  IOasisPatientLetterFilters
>;

export class PatientLetterSourceEntity extends BaseSourceEntity<OasisPatientLetter> {
  sourceEntity = PATIENT_LETTER_SOURCE_ENTITY;
  entityResourceType = PATIENT_LETTER_RESOURCE_TYPE;
  sourceQuery = [PATIENT_LETTER_SOURCE_QUERY, PATIENT_LETTER_SOURCE_QUERY_V8];
  verifySourceFn = isOasisPatientLetter;
  allowOffsetJob = true;

  migrationDestinations = [PATIENT_INTERACTION_DESTINATION_ENTITY.metadata.key];

  translate(data: IOasisPatientLetter): IOasisPatientCommuncationTranslations {
    return {
      createdAt: data.createdAt,
    };
  }

  getSourceRecordId(data: IOasisPatientLetter): string {
    return data.id.toString();
  }

  getSourceLabel(data: IOasisPatientLetter): string {
    return `${data.id} - ${data.createdAt}`;
  }

  getFilterData(
    data: IOasisPatientLetter,
    _timezone: Timezone
  ): IOasisPatientLetterFilters {
    return {
      patientId: data.patientId,
    };
  }
}
